import { Order, Store } from "../__generated__/graphql";
import {
  formatDateToYearMonthDayDayOfWeek,
  formatTimeWithAM,
} from "./date/format-date";

/**
 * 주문에 해당하는 매장 반환
 * @param order Order
 * @returns Store | undefined
 */
export function getStoreByOrder(order: Order): Store | undefined {
  // 예약팩
  if (order?.packId) {
    return order.pack?.store;
  }
  // 실시간팩
  else {
    return order?.products[0]?.store;
  }
}

/**
 * 주문에 해당하는 픽업 날짜 반환
 * @param order Order
 * @returns string
 */
export function getPickUpDateByOrder(order: Order): string {
  const orderType = getOrderType(order);

  if (orderType === "예약구매") {
    return getPreOrderPickUpDate(order);
  } else if (orderType === "실시간구매") {
    return getLiveOrderPickUpDate(order);
  } else {
    return "";
  }
}

function getPreOrderPickUpDate(order: Order): string {
  // MEMO: packPickupStartTime이 없는 legacy 데이터일 경우 주문 생성 날짜를 반환
  if (!order.packPickupStartTime || !order.packPickupEndTime)
    return formatDateToYearMonthDayDayOfWeek(new Date(order.createdAt));

  const pickUpDate = new Date(order.packPickupStartTime);
  return formatDateToYearMonthDayDayOfWeek(pickUpDate);
}

function getLiveOrderPickUpDate(order: Order): string {
  // MEMO: pickUpTime이 없는 legacy 데이터일 경우 주문 생성 날짜를 반환
  if (!order.pickUpTime)
    return formatDateToYearMonthDayDayOfWeek(new Date(order.createdAt));

  const [hours, mins] = order?.pickUpTime?.split(":")?.map(Number) || [];
  const date = new Date(order.createdAt);
  date.setHours(hours, mins, 0, 0);

  return formatDateToYearMonthDayDayOfWeek(date);
}

/**
 * 주문에 해당하는 픽업 시간 반환
 * 예약팩, 실시간팩 구분 후 반환
 * @param order Order
 * @returns string
 */
export function getPickUpTimeByOrder(order: Order): string {
  const orderType = getOrderType(order);

  if (orderType === "예약구매") {
    return getPreOrderPickUpTime(order);
  } else if (orderType === "실시간구매") {
    if (order.pickUpTime) {
      return getLiveOrderPickUpTime(order);
    } else {
      // NOTE: 실시간 팩에 pickUpTime이 도중에 추가되어 없는 경우 예외처리
      return "";
    }
  }
  return "";
}

/**
 * 예약팩 픽업 시간 반환
 * yyyy.mm.dd hh:mm ~ hh:mm
 * @param order Order
 * @returns string
 */
function getPreOrderPickUpTime(order: Order): string {
  if (!order.packPickupStartTime || !order.packPickupEndTime) return "";

  const today = new Date();
  const pickUpStartDate = new Date(order.packPickupStartTime);
  const pickUpEndDate = new Date(order.packPickupEndTime);

  const startHour = pickUpStartDate.getHours().toString().padStart(2, "0");
  const startMinute = pickUpStartDate.getMinutes().toString().padStart(2, "0");
  const startTime = formatTimeWithAM(`${startHour}:${startMinute}`);
  const endHour = pickUpEndDate.getHours().toString().padStart(2, "0");
  const endMinute = pickUpEndDate.getMinutes().toString().padStart(2, "0");
  const endTime = formatTimeWithAM(`${endHour}:${endMinute}`);

  // 내일 날짜 설정
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);

  // 오늘, 내일이면 prefix 추가
  if (today.toDateString() === pickUpStartDate.toDateString()) {
    return `${startTime} ~ ${endTime}`;
  } else if (tomorrow.toDateString() === pickUpStartDate.toDateString()) {
    return `${startTime} ~ ${endTime}`;
  } else {
    // 오늘, 내일이 아니면 날짜 표시
    return `${startTime} ~ ${endTime}`;
  }
}

/**
 * 실시간팩 픽업 시간 반환
 * yyyy.mm.dd hh:mm
 * @param order Order
 * @returns string
 */
function getLiveOrderPickUpTime(order: Order): string {
  if (!order.pickUpTime) return "";

  const [hours, mins] = order.pickUpTime.split(":")?.map(Number) || [];
  const hour = hours.toString().padStart(2, "0");
  const minute = mins.toString().padStart(2, "0");

  return `${hour}시 ${minute}분`;
}

/**
 * 주문이 예약팩인지 실시간팩인지 반환
 * @param order Order
 * @returns "예약구매" | "실시간구매" | undefined
 */
export function getOrderType(order: Order): "예약구매" | "실시간구매" | "" {
  if (order?.packId) {
    return "예약구매";
  } else if (order?.products?.length > 0) {
    return "실시간구매";
  }

  return "";
}

/**
 * 주문 날짜 오늘, 내일, 오늘 이전 반환
 * 예약팩 픽업 시작 시간 기준으로 오늘, 내일 여부 반환
 * 실시간 팩은 오늘일 경우에만 '오늘' 반환
 * @param order Order
 * @returns "오늘" | "내일" | ""
 */
export function getOrderDateStatus(order: Order): "오늘" | "내일" | "" {
  if (getOrderType(order) === "예약구매") {
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);

    const orderDate = new Date(order.packPickupStartTime);

    if (today.toDateString() === orderDate.toDateString()) {
      return "오늘";
    } else if (tomorrow.toDateString() === orderDate.toDateString()) {
      return "내일";
    } else {
      return "";
    }
  } else if (getOrderType(order) === "실시간구매") {
    const today = new Date();
    const orderDate = new Date(order.createdAt);
    if (today.toDateString() === orderDate.toDateString()) {
      return "오늘";
    }

    return "";
  }
  return "";
}
